import { useMemo, useState } from "react";
import { feedArticles, rankGroups, type Article } from "@/mocks/feed";

interface Props {
  onRead: (a: Article) => void;
}

export default function HotRank({ onRead }: Props) {
  const [active, setActive] = useState(rankGroups[0].key);

  const list = useMemo(() => {
    const base = active === "全网" ? feedArticles : feedArticles.filter((a) => a.category === active);
    return [...base].sort((a, b) => b.heat - a.heat).slice(0, 8);
  }, [active]);

  return (
    <section id="hot" className="w-full scroll-mt-20 bg-background-100/60">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <div>
              <p className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
                <i className="ri-fire-line" aria-hidden="true"></i> 实时热度
              </p>
              <h2 className="font-display mt-3 text-2xl md:text-4xl text-foreground-950">
                今日热榜 · 正在刷屏的瓜
              </h2>
              <p className="mt-2 text-sm text-foreground-600">按全网讨论热度实时排序，点击任一条目即可展开完整脉络。</p>
            </div>

            <div role="tablist" aria-label="热榜分类" className="inline-flex items-center rounded-full border border-background-200 bg-background-50 p-1 self-start sm:self-auto">
              {rankGroups.map((g) => (
                <button
                  key={g.key}
                  role="tab"
                  aria-selected={active === g.key}
                  type="button"
                  onClick={() => setActive(g.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors cursor-pointer ${active === g.key ? `${g.color} text-background-50` : "text-foreground-700 hover:text-primary-700"}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
            {list.map((item, idx) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onRead(item)}
                  className="group flex w-full items-center gap-4 rounded-2xl border border-background-200/70 bg-background-50 p-3 md:p-4 text-left hover:border-primary-200 hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <span className={`font-display shrink-0 w-10 text-2xl ${idx < 3 ? "text-primary-600" : "text-foreground-300"}`}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-foreground-600 bg-background-200/70">{item.category}</span>
                      <span className="text-xs text-foreground-500">
                        <i className="ri-eye-line mr-1 align-[-1px]" aria-hidden="true"></i>
                        {item.views} 热度
                      </span>
                    </div>
                    <h3 className="mt-1.5 line-clamp-2 text-sm md:text-[15px] font-medium leading-snug text-foreground-900 group-hover:text-primary-700 transition-colors">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-xs text-foreground-500 line-clamp-1">{item.source} · {item.time}</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    <div className="hidden sm:flex w-16 h-1.5 overflow-hidden rounded-full bg-background-200">
                      <div className={`h-full rounded-full ${idx < 3 ? "bg-primary-500" : "bg-accent-400"}`} style={{ width: `${item.heat}%` }}></div>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors" aria-hidden="true"></i>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}